'use client';

import type { RefObject } from 'react';
import { Calendar, Users, Baby, Search, Loader2 } from 'lucide-react';

interface ChildAge {
  id: string;
  age: '4-' | '5+';
}

interface SearchFiltersBarProps {
  // Fechas
  arrival: string;
  departure: string;
  nights: number;
  onToggleDatePicker: () => void;
  dateError?: boolean;
  dateButtonRef?: RefObject<HTMLButtonElement | null>;

  // Huéspedes
  totalAdults: number;
  smallChildren: number;
  onToggleGuestPicker: () => void;
  guestButtonRef?: RefObject<HTMLButtonElement | null>;

  // Búsqueda
  isLoading?: boolean;
  onSubmit?: () => void;

  // Variantes
  variant?: 'default' | 'with-input';
  inputValue?: string;
  inputPlaceholder?: string;
  onInputChange?: (value: string) => void;
  onInputFocus?: () => void;
}

export default function SearchFiltersBar({
  arrival,
  departure,
  nights,
  onToggleDatePicker,
  dateError = false,
  dateButtonRef,
  totalAdults,
  smallChildren,
  onToggleGuestPicker,
  guestButtonRef,
  isLoading = false,
  onSubmit,
  variant = 'default',
  inputValue = '',
  inputPlaceholder = '',
  onInputChange,
  onInputFocus,
}: SearchFiltersBarProps) {

  // Formatear fecha para mostrar
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="flex items-center w-full bg-white rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-200/80 p-1.5 transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500">

      {/* Filtros — grid 60/40 (Gemini 3.1 Pro 2026-05-06): el flexbox previo
          dejaba huéspedes aislado contra la lupa porque flex-1 en fechas +
          flex-shrink-0 en personas empujaba todo el espacio sobrante a la
          izquierda. Grid con minmax(0,3fr)/minmax(0,2fr) reparte 60/40 con
          ambos botones tomando peso visual proporcional. */}
      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] flex-1 min-w-0 divide-x divide-gray-100 pl-1 md:pl-2">

        {/* Botón de Fechas — active:bg-gray-100 da feedback táctil inmediato en mobile
            antes de que abra el popover (brief 2026-05-18 P0). */}
        <button
          ref={dateButtonRef}
          type="button"
          onClick={onToggleDatePicker}
          aria-label="Seleccionar fechas"
          style={{ touchAction: 'manipulation' }}
          className={`flex cursor-pointer items-center gap-2 px-2 md:px-4 py-2.5 rounded-full hover:bg-gray-50 active:bg-gray-100 transition-colors min-w-0 ${
            dateError ? 'ring-1 ring-red-500 bg-red-50' : ''
          }`}
        >
          <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" strokeWidth={2} />
          <div className="text-left truncate">
            {arrival ? (
              <span className="font-medium text-gray-900 text-sm truncate block">
                {formatDate(arrival)}
                {departure && (
                  <>
                    <span className="text-gray-400 font-normal mx-0.5"> → </span>
                    {formatDate(departure)}
                  </>
                )}
                <span className="hidden md:inline text-gray-500 font-normal ml-1">
                  · {nights} {nights === 1 ? 'noche' : 'noches'}
                </span>
              </span>
            ) : (
              <span className="font-medium text-gray-600 text-sm">Fechas</span>
            )}
          </div>
        </button>

        {/* Botón de Personas — sin flex-shrink-0; ahora el grid 60/40 le da
            su porción proporcional. justify-center centra el contenido para
            que no quede pegado al divider. */}
        <button
          ref={guestButtonRef}
          type="button"
          onClick={onToggleGuestPicker}
          aria-label="Seleccionar huéspedes"
          style={{ touchAction: 'manipulation' }}
          className="flex cursor-pointer items-center justify-center gap-2 px-3 md:px-4 py-2.5 rounded-full hover:bg-gray-50 active:bg-gray-100 transition-colors min-w-0"
        >
          <Users className="h-4 w-4 text-gray-500" strokeWidth={2} />
          <div className="text-sm flex items-center gap-1">
            <span className="font-medium text-gray-900">{totalAdults}</span>
            <span className="text-gray-500 hidden sm:inline">pers.</span>
            {smallChildren > 0 && (
              <>
                <span className="text-gray-300 mx-0.5">+</span>
                <Baby className="h-3.5 w-3.5 text-gray-500" strokeWidth={2} />
                <span className="font-medium text-gray-900">{smallChildren}</span>
              </>
            )}
          </div>
        </button>
      </div>

      {/* Input opcional (variante with-input) */}
      {variant === 'with-input' && (
        <div className="flex-1 min-w-0 px-2 border-l border-gray-100">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => onInputChange?.(e.target.value)}
            placeholder={inputPlaceholder}
            aria-label="Búsqueda adicional"
            className="w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none truncate"
            onFocus={onInputFocus}
          />
        </div>
      )}

      {/* Botón Buscar — circular unificado mobile + desktop. Loading reemplaza icono sin layout shift. */}
      <button
        type={onSubmit ? 'button' : 'submit'}
        onClick={onSubmit}
        disabled={isLoading}
        aria-busy={isLoading}
        aria-label="Buscar disponibilidad"
        className="flex-shrink-0 flex cursor-pointer items-center justify-center w-11 h-11 md:w-12 md:h-12 ml-1 rounded-full bg-primary-600 text-white hover:bg-primary-700 hover:-translate-y-px active:scale-95 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-white/90" />
        ) : (
          <Search className="h-5 w-5" strokeWidth={2.5} />
        )}
      </button>
    </div>
  );
}
